import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';

interface LegalSection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
  contactLink?: boolean;
}

@Component({
  selector: 'app-disclaimer',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './disclaimer.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../terms/terms.scss']
})
export class Disclaimer {
  readonly lastUpdated = '7 August 2026';

  readonly sections: LegalSection[] = [
    {
      id: 'not-advice',
      title: '1. Not financial advice',
      paragraphs: [
        'All calculations, outputs, and document templates provided by this website are for educational and informational purposes only. They do not constitute official financial, tax, or legal advice.',
        'Everything on everyrupeespent.com — our calculators, the AI Advisor, the Rental Agreement Generator and other document templates, articles and any figures or documents they produce — is provided for general information and educational purposes only. It is not, and must not be relied on as, financial, investment, tax, accounting, insurance or legal advice.',
        'We are not a SEBI-registered investment adviser, a research analyst, a distributor of financial products, or your financial advisor, and no document template we provide is a substitute for review by a licensed advocate. Nothing on this site is a recommendation, solicitation or offer to buy, sell or hold any financial product, security or investment.'
      ]
    },
    {
      id: 'estimates',
      title: '2. Results are estimates',
      paragraphs: [
        'Our calculators use standard formulas and the inputs you provide to produce illustrative estimates. Real-world outcomes will differ. Actual returns, EMIs, interest, taxes and maturity values depend on factors we cannot know or control — the exact terms of your bank or lender, day-count and compounding conventions, changes in interest rates, market performance, changes in tax law and slabs, fees, rounding, and your own circumstances.',
        'Interest rates, tax rules (including slabs, deductions and exemptions), and scheme parameters such as PPF, EPF and SSY rates change over time. We update our calculators periodically but do not guarantee that every figure reflects the latest applicable rate or rule at the moment you use it. Always confirm current numbers with your bank, the relevant authority, or a qualified professional.'
      ]
    },
    {
      id: 'document-templates',
      title: '3. About document templates',
      paragraphs: [
        'Where the site offers a document template — such as the Rental Agreement Generator — the output is a drafting aid built from a standard clause template and the details you provide. It is not reviewed by a lawyer before it reaches you, is not a substitute for legal advice, and may not be suitable for your specific situation.',
        'A generated document is not automatically registered, e-Stamped or legally executed. Where registration, stamping or execution is required by law, that remains your responsibility. We recommend having any document template reviewed by a qualified advocate before you sign or rely on it.'
      ]
    },
    {
      id: 'ai-advisor',
      title: '4. About the AI Advisor',
      paragraphs: [
        'Where a calculator offers an "AI Advisor" feature, the observations shown are generated by a fixed set of rules built into the calculator — checks like your EMI-to-income ratio, loan-to-income ratio, and tenure versus retirement age — run entirely in your browser from the inputs you enter. No AI model is called and no data is sent to us or a third party for this feature.',
        'These rule-based observations are a general rule-of-thumb check, not personalized advice, and may not account for factors specific to your situation. Treat them as a starting point for your own research and verify anything important before acting on it.'
      ]
    },
    {
      id: 'no-guarantee',
      title: '5. No warranty or guarantee',
      paragraphs: [
        'While we work hard to keep our calculators accurate and up to date, we make no warranty or guarantee, express or implied, about the accuracy, completeness, reliability, suitability or availability of the site or its content. The Services are provided "as is" and "as available".'
      ]
    },
    {
      id: 'your-responsibility',
      title: '6. Your decisions are your own',
      paragraphs: [
        'Any decision you make based on information from this site is your sole responsibility. Before making a financial, investment, tax or insurance decision, you should do your own research and consult a qualified, licensed professional — such as a SEBI-registered investment adviser, a chartered accountant, or a certified financial planner — who can consider your full personal circumstances.',
        'To the maximum extent permitted by law, Every Rupee Spent is not liable for any loss or damage arising from your use of, or reliance on, this site or its content. This Disclaimer forms part of, and should be read together with, our Terms of Service.'
      ]
    },
    {
      id: 'external-links',
      title: '7. External links and third parties',
      paragraphs: [
        'The site may reference or link to third-party websites, products or services for convenience. We do not endorse and are not responsible for the content, accuracy or practices of any third party, including the terms of any bank, lender, fund house or insurer you may deal with.'
      ]
    },
    {
      id: 'contact',
      title: '8. Questions',
      paragraphs: [
        'If you spot an error in a calculator or have a question about this Disclaimer, please tell us — we take accuracy seriously and will look into it.'
      ],
      contactLink: true
    }
  ];
}
